import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Search,
  Filter,
  School,
  GraduationCap,
  BookOpen,
  Building2,
  MapPin,
  Phone,
  Mail,
  Video,
  Facebook,
  Instagram,
  Twitter
} from 'lucide-react';
import { admissionsService } from '../../firebase/admissions';
import toast from 'react-hot-toast';
import R2Image from '../../components/R2Image';

const InstitutionManagement = () => {
  const [institutions, setInstitutions] = useState([]);
  const [filteredInstitutions, setFilteredInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingInstitution, setEditingInstitution] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'school',
    description: '',
    logo: '',
    applicationFee: 500,
    address: {
      street: '',
      city: '',
      state: '',
      pincode: ''
    },
    contact: {
      phone: '',
      email: '',
      website: ''
    },
    socialMedia: {
      facebook: '',
      instagram: '',
      twitter: ''
    },
    established: '',
    students: '',
    virtualTourVideo: '',
    isActive: true
  });

  useEffect(() => {
    loadInstitutions();
  }, []);

  useEffect(() => {
    filterInstitutions();
  }, [searchTerm, filterType, institutions]);

  const loadInstitutions = async () => {
    try {
      setLoading(true);
      const result = await admissionsService.getAllInstitutions();
      
      if (result.success) {
        setInstitutions(result.institutions || []);
      } else {
        toast.error('Failed to load institutions');
      }
    } catch (error) {
      toast.error('Error loading institutions');
    } finally {
      setLoading(false);
    }
  };

  const filterInstitutions = () => {
    let filtered = [...institutions];

    if (filterType !== 'all') {
      filtered = filtered.filter(inst => inst.type === filterType);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(inst =>
        inst.name?.toLowerCase().includes(term) ||
        inst.description?.toLowerCase().includes(term) ||
        inst.address?.city?.toLowerCase().includes(term)
      );
    }

    setFilteredInstitutions(filtered);
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = editingInstitution
        ? await admissionsService.saveInstitution(formData, editingInstitution.id)
        : await admissionsService.saveInstitution(formData);

      if (result.success) {
        toast.success(editingInstitution ? 'Institution updated' : 'Institution created');
        setShowForm(false);
        setEditingInstitution(null);
        resetForm();
        loadInstitutions();
      } else {
        toast.error('Failed to save institution');
      }
    } catch (error) {
      toast.error('Error saving institution');
    }
  };

  const handleEdit = (institution) => {
    setEditingInstitution(institution);
    setFormData({
      name: institution.name || '',
      type: institution.type || 'school',
      description: institution.description || '',
      logo: institution.logo || '',
      applicationFee: institution.applicationFee || 500,
      address: institution.address || { street: '', city: '', state: '', pincode: '' },
      contact: institution.contact || { phone: '', email: '', website: '' },
      socialMedia: institution.socialMedia || { facebook: '', instagram: '', twitter: '' },
      established: institution.established || '',
      students: institution.students || '',
      virtualTourVideo: institution.virtualTourVideo || '',
      isActive: institution.isActive !== undefined ? institution.isActive : true
    });
    setShowForm(true);
  };

  const handleToggleVisibility = async (institution) => {
    try {
      const newStatus = !institution.isActive;
      const result = await admissionsService.toggleInstitutionVisibility(institution.id, newStatus);
      
      if (result.success) {
        toast.success(newStatus ? 'Institution is now visible' : 'Institution is now hidden');
        loadInstitutions();
      } else {
        toast.error('Failed to update visibility: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Error updating visibility: ' + error.message);
    }
  };

  const handleDelete = async (institutionId, institutionName) => {
    const confirmMessage = `Are you sure you want to delete "${institutionName}"?\n\nThis will permanently delete:\n- Institution profile\n- All admission applications\n- All curriculum data\n- All materials/brochures\n- All faculty information\n- All customizations\n- All payment records\n\nThis action cannot be undone!`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const result = await admissionsService.deleteInstitution(institutionId);
      
      if (result.success) {
        toast.success('Institution and all related data deleted successfully');
        loadInstitutions();
      } else {
        toast.error('Failed to delete institution: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Error deleting institution: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'school',
      description: '',
      logo: '',
      applicationFee: 500,
      address: { street: '', city: '', state: '', pincode: '' },
      contact: { phone: '', email: '', website: '' },
      socialMedia: { facebook: '', instagram: '', twitter: '' },
      established: '',
      students: '',
      virtualTourVideo: '',
      isActive: true
    });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'school':
        return <School className="w-5 h-5" />;
      case 'college':
        return <GraduationCap className="w-5 h-5" />;
      case 'coaching':
        return <BookOpen className="w-5 h-5" />;
      default:
        return <Building2 className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Institution Management</h1>
            <p className="text-gray-600 mt-1">Manage schools, colleges, and coaching centers</p>
            </div>
            <button
              onClick={() => {
                resetForm();
                setEditingInstitution(null);
                setShowForm(true);
              }}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-5 h-5" />
              Add Institution
            </button>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search institutions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="all">All Types</option>
                <option value="school">Schools</option>
                <option value="college">Colleges</option>
                <option value="coaching">Coachings</option>
              </select>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Institutions</p>
                  <p className="text-2xl font-bold text-gray-900">{filteredInstitutions.length}</p>
                </div>
                <Building2 className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Visible in Admissions</p>
                  <p className="text-2xl font-bold text-green-600">
                    {filteredInstitutions.filter(i => i.isActive !== false).length}
                  </p>
                </div>
                <Eye className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Hidden from Admissions</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {filteredInstitutions.filter(i => i.isActive === false).length}
                  </p>
                </div>
                <EyeOff className="w-8 h-8 text-yellow-600" />
              </div>
            </div>
          </div>

          {/* Institutions List */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredInstitutions.map((institution) => (
              <motion.div
                key={institution.id}
                whileHover={{ y: -5 }}
                className="bg-white rounded-lg shadow-md overflow-hidden"
              >
                <div className="h-32 bg-gradient-to-br from-blue-400 to-purple-500 relative">
                  {institution.logo && (
                    <R2Image
                      fileKey={institution.logo}
                      alt={institution.name}
                      className="w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute top-2 right-2 flex items-center gap-2">
                    <div className="bg-white px-2 py-1 rounded flex items-center gap-1">
                      {getTypeIcon(institution.type)}
                      <span className="capitalize text-xs">{institution.type}</span>
                    </div>
                    {institution.isActive === false && (
                      <div className="bg-red-500 text-white px-2 py-1 rounded text-xs">
                        Hidden
                      </div>
                    )}
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-lg mb-2">{institution.name}</h3>
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                    {institution.description}
                  </p>
                  {institution.address && (
                    <div className="flex items-center text-gray-500 text-xs mb-2">
                      <MapPin className="w-3 h-3 mr-1" />
                      {institution.address.city}
                    </div>
                  )}
                  <div className="flex flex-col gap-2 mt-4">
                    <button
                      onClick={() => handleEdit(institution)}
                      className="w-full px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center justify-center gap-2"
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleToggleVisibility(institution)}
                      className={`w-full px-3 py-2 text-sm rounded flex items-center justify-center gap-2 ${
                        institution.isActive !== false
                          ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                      title={institution.isActive !== false ? 'Hide from users' : 'Show to users'}
                    >
                      {institution.isActive !== false ? (
                        <>
                          <EyeOff className="w-4 h-4" />
                          Hide from Admissions
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4" />
                          Show in Admissions
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleDelete(institution.id, institution.name)}
                      className="w-full px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 flex items-center justify-center gap-2"
                      title="Delete institution and all admission data"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {filteredInstitutions.length === 0 && (
            <div className="text-center py-12">
              <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No institutions found</p>
            </div>
          )}

          {/* Form Modal */}
          {showForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              >
                <div className="p-6">
                <h2 className="text-2xl font-bold mb-4">
                  {editingInstitution ? 'Edit Institution' : 'Add Institution'}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type *
                    </label>
                    <select
                      required
                      value={formData.type}
                      onChange={(e) => handleInputChange('type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="school">School</option>
                      <option value="college">College</option>
                      <option value="coaching">Coaching</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Application Fee (₹)
                    </label>
                    <input
                      type="number"
                      value={formData.applicationFee}
                      onChange={(e) => handleInputChange('applicationFee', parseFloat(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={formData.contact.phone}
                        onChange={(e) => handleInputChange('contact.phone', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={formData.contact.email}
                        onChange={(e) => handleInputChange('contact.email', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Street Address
                    </label>
                    <input
                      type="text"
                      value={formData.address.street}
                      onChange={(e) => handleInputChange('address.street', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City
                      </label>
                      <input
                        type="text"
                        value={formData.address.city}
                        onChange={(e) => handleInputChange('address.city', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State
                      </label>
                      <input
                        type="text"
                        value={formData.address.state}
                        onChange={(e) => handleInputChange('address.state', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pincode
                      </label>
                      <input
                        type="text"
                        value={formData.address.pincode}
                        onChange={(e) => handleInputChange('address.pincode', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <div className="flex items-center gap-2">
                        <Video className="w-4 h-4" />
                        Virtual Tour Video URL
                      </div>
                    </label>
                    <input
                      type="url"
                      value={formData.virtualTourVideo}
                      onChange={(e) => handleInputChange('virtualTourVideo', e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Simply paste any YouTube video URL (watch or youtu.be format) - no embedding needed
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Social Media Links
                    </label>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-2">
                          <Facebook className="w-4 h-4 text-blue-600" />
                          Facebook URL
                        </label>
                        <input
                          type="url"
                          value={formData.socialMedia?.facebook || ''}
                          onChange={(e) => handleInputChange('socialMedia.facebook', e.target.value)}
                          placeholder="https://www.facebook.com/..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-2">
                          <Instagram className="w-4 h-4 text-pink-600" />
                          Instagram URL
                        </label>
                        <input
                          type="url"
                          value={formData.socialMedia?.instagram || ''}
                          onChange={(e) => handleInputChange('socialMedia.instagram', e.target.value)}
                          placeholder="https://www.instagram.com/..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-2">
                          <Twitter className="w-4 h-4 text-sky-500" />
                          Twitter URL
                        </label>
                        <input
                          type="url"
                          value={formData.socialMedia?.twitter || ''}
                          onChange={(e) => handleInputChange('socialMedia.twitter', e.target.value)}
                          placeholder="https://www.twitter.com/... or https://x.com/..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => handleInputChange('isActive', e.target.checked)}
                      className="w-4 h-4"
                    />
                    <label className="text-sm text-gray-700">Active</label>
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="submit"
                      className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                    >
                      {editingInstitution ? 'Update' : 'Create'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowForm(false);
                        setEditingInstitution(null);
                        resetForm();
                      }}
                      className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstitutionManagement;

